import { useLogger } from 'lib/logger';
import { createContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface DrawerDataProps {
  /** Drawer to show */
  drawer?: string | undefined,

  /** Customer */
  customerId?: number,

  /** Resources */
  companyGuid?: string,
  customerGuid?: string,
  workOrderGuid?: string,
  documentGuid?: string,
  jobGuid?: string,
  jobGuids?: [string],
  dailyFinancialsGuid?: string,
  invoiceGuid?: string,
  storageGuid?: string,
  storageSpaceGuid?: string,
  fieldGuid?: string,
  actionGuid?: string,
  userGuid?: string,
  pnlGuid?: string,
  cashFlowGuid?: string,
  employeeGuid?: string,
  payrollGuid?: string,
  noteGuid?: string,
  timeOffGuid?: string,

  /** Resource */
  resource?: string,

  /** This could be any of the above, sometimes it's a generic request */
  resourceGuid?: string | string[],

  // Use this to send multiple emails, handle multiple operations in a drawer
  resources?: Array<any>[],

  /** Additional parameters mapped by extending these props */
  [key: string]: any
}

interface ApplicationContextType {
  showError(err?: any, val?: any | undefined): void;
  showMessage(message: any, title: string, size?: any): void;
  showConfirmation(message: any, title: string, callback: () => void): void;
  hideError(): void;
  errorIsVisible: boolean;
  messageBody: any;
  messageTitle: string;
  buttons: Array<string>;
  size: any;
  confirmationCallback: () => void,
  showDrawer(drawer: any, data?: DrawerDataProps, onUpdate?: (data: any) => void): void, // see env DRAWER types
  drawerData: DrawerDataProps,
  onUpdateDrawerCallback: (data: any) => void
}

const ApplicationContext = createContext<ApplicationContextType>({
  showError: () => { },
  showMessage: () => { },
  showConfirmation: () => { },
  hideError: () => { },
  errorIsVisible: false,
  messageBody: '',
  messageTitle: '',
  buttons: [],
  size: 'xs',
  confirmationCallback: () => { },
  showDrawer: () => { },
  drawerData: { },
  onUpdateDrawerCallback: (data: any) => { }
});

const ApplicationProvider = ({ children }: any) => {
  const logger = useLogger();
  const [errorIsVisible, setErrorIsVisible] = useState(false);
  const [messageBody, setMesageBody] = useState<any>('');
  const [messageTitle, setMessageTitle] = useState('');
  const [size, setSize] = useState<any>('xs');
  const [buttons, setButtons] = useState(['close']);
  const [confirmationCallback, setConfirmationCallback] = useState<any>(undefined);
  const [drawerData, setDrawerData] = useState<any>(undefined);
  const [onUpdateDrawerCallback, setOnUpdateDrawerCallback] = useState<any>(undefined);
  const history = useHistory();

  const showError = (err?: any, msg?: any | undefined) => {
    // if (err.response) {
    //   logger.error(err);
    // } else {
    //   logger.error({}, err);
    // }

    setMesageBody(msg || <p>Application raised an error. Refresh and try again while administrators investigate the issue.</p>);
    setMessageTitle('Something went wrong');
    setErrorIsVisible(true);
    setButtons(['close', 'close_feedback']);
  }

  const showMessage = (message: any, title: string, size?: any) => {
    setMesageBody(message);
    setMessageTitle(title);
    setErrorIsVisible(true);
    setSize(size || 'xs');
    setButtons(['ok']);
  }

  const showConfirmation = (message: any, title: string, callback: () => void) => {
    setMesageBody(message);
    setMessageTitle(title);
    setErrorIsVisible(true);
    setButtons(['cancel', 'ok']);
    setConfirmationCallback(() => callback);
  }

  const hideError = () => {
    setErrorIsVisible(false);
  }

  const showDrawer = (drawer: string | undefined, data: any, onUpdate?: (data: any) => void) => {
    if (drawer === undefined) {
      setDrawerData({ drawer: undefined });
      setOnUpdateDrawerCallback(undefined);
      // history.replace(`${window.location.pathname}`);
    } else {
      setDrawerData({ drawer, ...data });
      // history.replace(`${window.location.pathname}?${queryString.stringify({ ...data })}`);
    }

    if (onUpdate !== undefined) {
      setOnUpdateDrawerCallback(() => onUpdate);
    }
  }

  return (
    <ApplicationContext.Provider value={{
      showError,
      showMessage,
      showConfirmation,
      hideError,
      showDrawer,
      confirmationCallback,
      errorIsVisible,
      messageBody,
      messageTitle,
      buttons,
      size,
      drawerData,
      onUpdateDrawerCallback
    }}>
      {children}
    </ApplicationContext.Provider>
  );
}

const ConditionalWrapper = ({ condition, wrapper, children }: any) => condition ? wrapper(children) : children;

export {
  ApplicationContext,
  ApplicationProvider,
  ConditionalWrapper
}

export type {
  DrawerDataProps
}
